import React from 'react';
import {
  Binary,
  Box,
  Code2,
  Image as ImageIcon,
  Loader2,
  MessageSquare,
  Mic,
  Music,
  Paperclip,
  Send,
  Square,
  Upload,
  Video,
  Volume2,
  X,
  FileText,
  Film,
  File as FileIcon,
  Copy,
  Check,
  RotateCcw,
  GitFork,
  Pencil,
  Trash2,
  Settings2,
  Search,
  ChevronDown,
  ChevronUp,
  LogIn,
} from 'lucide-react';
import { CodeBlock } from './CodeBlock';
import { uploadFile } from './uploadFile';
import { DOCUMENT_ACCEPT, extractDocument } from './extractDocument';
import { GATEWAY_DEMOS, type GatewayDemo } from './gatewayDemos';
import { getVideoParamSpec, EMPTY_ADVANCED, type VideoAdvancedValues } from './videoModelParams';
import { buildVideoPayload } from './videoPayload';
import { buildGatewaySamples, samplesToMarkdown, CODE_LANGS, codeLangHl, type CodeKind } from './gatewayCode';

// Lazy: pulls in firebase/auth. Only loaded when a 401 needs the sign-in modal.
const SignInModal = React.lazy(() => import('./SignInModal'));

// GatewayPlayground runs real requests against the app.nz gateway from the
// browser, one surface per modality (chat, image, embeddings, video, music,
// speech, transcription, image→3d). Each surface renders a live preview of the
// result alongside the raw JSON the API returned. It is lazy-loaded by
// GatewayPage so it never ships in the homepage bundle. Auth is implicit:
// same-origin fetch sends the session cookie for signed-in users; anyone else
// can paste an API key (persisted to localStorage).

type Modality =
  | 'chat'
  | 'image'
  | 'embeddings'
  | 'video'
  | 'music'
  | 'speech'
  | 'transcription'
  | '3d';

interface CatalogModel {
  id: string;
  provider: string;
  modality?: string;
  supported_sizes?: string[];
  input_price_per_1m?: number;
  output_price_per_1m?: number;
  input_cache_hit_per_1m?: number;
  price_per_image?: number;
  price_per_video?: number;
  price_per_request?: number;
  price_per_minute?: number;
  supports_streaming?: boolean;
  supports_vision?: boolean;
  context_window?: number;
  max_output_tokens?: number;
}

interface Props {
  models: CatalogModel[];
  origin: string;
  initialTab?: Modality;
  initialModel?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  createdAt?: number;
  attachments?: ChatAttachment[];
  branchOf?: number;
}

type ChatAttachmentKind = 'image' | 'document' | 'video' | 'audio' | 'file';

interface ChatAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  url?: string;
  kind: ChatAttachmentKind;
  extractedText?: string;
  extractedPreview?: string;
}

type MediaKind = 'video' | 'audio' | 'image' | 'model' | '';

interface RunResult {
  kind: Modality;
  json?: unknown;
  raw?: string;
  text?: string;
  images?: string[];
  embedding?: number[];
  mediaUrl?: string;
  mediaKind?: MediaKind;
  blobUrl?: string;
  blobNote?: string;
}

const TABS: { key: Modality; label: string; icon: React.ComponentType<{ size?: number }> }[] = [
  { key: 'chat', label: 'Chat', icon: MessageSquare },
  { key: 'image', label: 'Image', icon: ImageIcon },
  { key: 'embeddings', label: 'Embeddings', icon: Binary },
  { key: 'video', label: 'Video', icon: Video },
  { key: 'music', label: 'Music', icon: Music },
  { key: 'speech', label: 'Speech', icon: Volume2 },
  { key: 'transcription', label: 'Transcribe', icon: Mic },
  { key: '3d', label: 'Image → 3D', icon: Box },
];

const SAMPLE_I2V_IMAGES = [
  { label: 'Mountain lake', src: 'https://picsum.photos/seed/i2v1/1280/720' },
  { label: 'City street', src: 'https://picsum.photos/seed/i2v2/1280/720' },
  { label: 'Forest path', src: 'https://picsum.photos/seed/i2v3/1280/720' },
  { label: 'Ocean sunset', src: 'https://picsum.photos/seed/i2v4/1280/720' },
];

const ENDPOINT: Record<Modality, string> = {
  chat: '/v1/chat/completions',
  image: '/v1/images/generations',
  embeddings: '/v1/embeddings',
  video: '/v1/videos/generations',
  music: '/v1/music/generations',
  speech: '/v1/audio/speech',
  transcription: '/v1/audio/transcriptions',
  '3d': '/v1/3d/generations',
};

function extractTranscript(payload: unknown): string {
  if (typeof payload === 'string') return payload.trim();
  if (!payload || typeof payload !== 'object') return '';
  const record = payload as Record<string, unknown>;
  for (const key of ['text', 'transcript', 'transcription', 'output']) {
    const text = extractTranscript(record[key]);
    if (text) return text;
  }
  return '';
}

// Fallback modality inference for when a cached /api/gateway/models response
// predates the server-side `modality` field. Mirrors server/gateway.go.
function inferModality(id: string): Modality {
  const i = id.toLowerCase();
  const has = (...s: string[]) => s.some((x) => i.includes(x));
  if (has('transcribe', 'whisper', 'stt')) return 'transcription';
  if (has('embed', 'rerank')) return 'embeddings';
  if (has('tts', 'speech-', 'voice')) return 'speech';
  if (has('3d', 'mesh', 'tripo', 'trellis', 'rigging')) return '3d';
  if (has('music', 'lyria')) return 'music';
  if (has('video', 'sora', 'hailuo', 'seedance', 'veo', 'kling', 'ltx', 'ra2v', 'wan', 'happy-horse', 'pika', 'luma', 'ray-2', 'ray/', 'pixverse', 'vidu', 'cogvideo', 'hunyuan', 'kandinsky', 'cosmos', 'lucy', 'gemini-omni')) return 'video';
  if (has('image', 'flux', 'dall-e', 'imagine', 'zimage', 'klein', 'ra1', 'diffusion')) return 'image';
  return 'chat';
}

const MEDIA_EXT: Record<Exclude<MediaKind, ''>, string[]> = {
  video: ['mp4', 'webm', 'mov', 'm4v'],
  audio: ['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'],
  image: ['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif'],
  model: ['glb', 'gltf', 'usdz', 'obj', 'ply', 'fbx', 'splat'],
};

function classifyUrl(u: string): MediaKind {
  const m = u.split('?')[0].toLowerCase().match(/\.([a-z0-9]+)$/);
  const ext = m ? m[1] : '';
  for (const kind of Object.keys(MEDIA_EXT) as Exclude<MediaKind, ''>[]) {
    if (MEDIA_EXT[kind].includes(ext)) return kind;
  }
  return '';
}

// findMedia walks a job/result envelope and returns the most likely renderable
// asset URL — extension wins, then a key-name hint, then the first URL seen.
function findMedia(obj: unknown): { url: string; kind: MediaKind } | null {
  const urls: { key: string; url: string }[] = [];
  const walk = (v: unknown, key: string) => {
    if (v == null) return;
    if (typeof v === 'string') {
      if (/^https?:\/\//.test(v)) urls.push({ key, url: v });
      return;
    }
    if (Array.isArray(v)) {
      v.forEach((x) => walk(x, key));
      return;
    }
    if (typeof v === 'object') {
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) walk(val, k.toLowerCase());
    }
  };
  walk(obj, '');
  for (const { url } of urls) {
    const kind = classifyUrl(url);
    if (kind) return { url, kind };
  }
  for (const { key, url } of urls) {
    if (/video/.test(key)) return { url, kind: 'video' };
    if (/audio|speech|music|sound/.test(key)) return { url, kind: 'audio' };
    if (/image|thumb|preview/.test(key)) return { url, kind: 'image' };
    if (/model|mesh|glb|asset|3d/.test(key)) return { url, kind: 'model' };
  }
  return urls.length ? { url: urls[0].url, kind: '' } : null;
}

function fmtCost(usd: number): string {
  if (usd <= 0) return '$0.00';
  if (usd < 0.0001) return '<$0.0001';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(3)}`;
}

function estimateTokens(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return Math.max(1, Math.ceil(trimmed.length / 4));
}

function estimateChatTokens(messages: ChatMessage[], system: string): number {
  return estimateTokens(system) + messages.reduce((sum, m) => {
    const attachmentTokens = (m.attachments || []).reduce((acc, att) => {
      const extracted = estimateTokens(att.extractedText || '');
      const media = att.kind === 'image' ? 512 : att.kind === 'video' || att.kind === 'audio' ? 256 : 24;
      return acc + extracted + media;
    }, 0);
    return sum + estimateTokens(m.content) + attachmentTokens + 4;
  }, 0);
}

function fmtTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(tokens >= 10_000 ? 0 : 1)}K`;
  return tokens.toLocaleString();
}

function inferAttachmentKind(file: File): ChatAttachmentKind {
  const mime = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (DOCUMENT_ACCEPT.split(',').some((ext) => name.endsWith(ext.trim().toLowerCase()))) return 'document';
  if (mime.startsWith('text/') || /(\.txt|\.md|\.csv|\.json|\.jsonl|\.xml|\.html|\.css|\.js|\.ts|\.tsx|\.py|\.go|\.rs|\.sql|\.log)$/i.test(name)) return 'document';
  return 'file';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

function attachmentSummary(att: ChatAttachment): string {
  const location = att.url ? `: ${att.url}` : ' (text extracted locally; no public URL)';
  return `${att.name} (${att.type || att.kind}, ${formatBytes(att.size)})${location}`;
}

function buildAttachmentText(attachments: ChatAttachment[]): string {
  if (!attachments.length) return '';
  const lines = ['Attached files:'];
  for (const att of attachments) {
    lines.push(`- ${attachmentSummary(att)}`);
    if (att.extractedText) {
      lines.push(`\nExtracted text from ${att.name}:\n\`\`\`\n${att.extractedText.slice(0, 120_000)}${att.extractedText.length > 120_000 ? '\n\n[Truncated after 120,000 characters.]' : ''}\n\`\`\``);
    } else if (att.kind === 'video') {
      lines.push('  Video is attached as a public URL for video-capable models.');
    }
  }
  return lines.join('\n');
}

function messageForAPI(message: ChatMessage): { role: ChatMessage['role']; content: string | any[] } {
  const attachments = message.attachments || [];
  if (!attachments.length) return { role: message.role, content: message.content };

  const parts: any[] = [];
  if (message.content.trim()) parts.push({ type: 'text', text: message.content });

  const textContext = buildAttachmentText(attachments.filter((att) => att.kind !== 'image' && !(att.kind === 'video' && att.url)));
  if (textContext) parts.push({ type: 'text', text: textContext });

  for (const att of attachments) {
    if (att.kind === 'image' && att.url) parts.push({ type: 'image_url', image_url: { url: att.url } });
    if (att.kind === 'video' && att.url) parts.push({ type: 'video_url', video_url: { url: att.url } });
  }

  if (!parts.length) parts.push({ type: 'text', text: buildAttachmentText(attachments) });
  return { role: message.role, content: parts };
}

function playCompletionSound() {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.04, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    gain.connect(ctx.destination);
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(720, now);
    osc.frequency.exponentialRampToValueAtTime(980, now + 0.08);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.2);
    window.setTimeout(() => void ctx.close(), 300);
  } catch {
    /* browser audio unavailable */
  }
}

function makeChatMessage(
  role: ChatMessage['role'],
  content: string,
  extras: Omit<ChatMessage, 'role' | 'content' | 'createdAt'> = {},
): ChatMessage {
  return { role, content, createdAt: Date.now(), ...extras };
}

function messageTime(ts?: number): string {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const inputCx =
  'h-10 w-full rounded-lg border border-white/10 bg-[#050505] px-3 text-sm text-white placeholder:text-white/30 focus:border-white/25 focus:outline-none';

export const GatewayPlayground: React.FC<Props> = ({ models, origin, initialTab, initialModel }) => {
  const [apiKey, setApiKey] = React.useState('');
  const [tab, setTab] = React.useState<Modality>(initialTab ?? 'chat');
  const [showCode, setShowCode] = React.useState(false);
  const [resultView, setResultView] = React.useState<'preview' | 'json'>('preview');

  // Session state + sign-in-on-401: a 401 opens the in-page sign-in modal and
  // the interrupted request re-runs after auth instead of dead-ending on an
  // error string.
  const [signedIn, setSignedIn] = React.useState<boolean | null>(null);
  const [showSignIn, setShowSignIn] = React.useState(false);
  const [showSettings, setShowSettings] = React.useState(false);
  const [systemExpanded, setSystemExpanded] = React.useState(false);
  const [galleryRefresh, setGalleryRefresh] = React.useState(0);
  const retryRef = React.useRef<(() => void) | null>(null);

  // Shared request lifecycle.
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [latencyMs, setLatencyMs] = React.useState<number | null>(null);
  const [result, setResult] = React.useState<RunResult | null>(null);

  // Chat (streaming) is its own little world.
  const [system, setSystem] = React.useState('You are a helpful assistant.');
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = React.useState(false);
  const [usage, setUsage] = React.useState<{ prompt: number; completion: number; cached?: number } | null>(null);
  const [maxTokens, setMaxTokens] = React.useState(4096);
  const [chatAttachments, setChatAttachments] = React.useState<ChatAttachment[]>([]);
  const [chatUploading, setChatUploading] = React.useState(false);
  const [chatUploadError, setChatUploadError] = React.useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = React.useState(false);

  // Per-surface inputs.
  const [model, setModel] = React.useState(initialModel ?? 'auto');
  const [chatInput, setChatInput] = React.useState('Explain what an AI model gateway is in two sentences.');
  const [prompt, setPrompt] = React.useState('A beige coffee mug on a wooden table, soft natural light');
  const [size, setSize] = React.useState('1024x1024');
  const [embedInput, setEmbedInput] = React.useState('app.nz routes every model through one API');
  const [voice, setVoice] = React.useState('eve');
  const [ttsInput, setTtsInput] = React.useState('Welcome to app.nz. One API key routes every model.');
  const [language, setLanguage] = React.useState('');

  // Uploaded asset URL for image-conditioned surfaces (video, 3d).
  const [assetUrl, setAssetUrl] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const [audioFile, setAudioFile] = React.useState<File | null>(null);

  // Video generation args (per-model fal params).
  const [videoResolution, setVideoResolution] = React.useState('720p');
  const [videoDuration, setVideoDuration] = React.useState('4');
  const [videoAspect, setVideoAspect] = React.useState('16:9');
  const [videoAudio, setVideoAudio] = React.useState(false);
  const [videoAdvanced, setVideoAdvanced] = React.useState<VideoAdvancedValues>(EMPTY_ADVANCED);
  const [showVideoAdvanced, setShowVideoAdvanced] = React.useState(false);

  // Code panel: language switcher + markdown copy.
  const [codeLang, setCodeLang] = React.useState<CodeKind>('python');
  const [copiedMd, setCopiedMd] = React.useState(false);

  const abortRef = React.useRef<AbortController | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const blobRef = React.useRef<string | null>(null);
  const chatFileInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    try {
      const k = localStorage.getItem('appnz_gateway_key');
      if (k) setApiKey(k);
      setSoundEnabled(localStorage.getItem('appnz_gateway_sound') === '1');
    } catch {
      /* storage blocked */
    }
  }, []);

  React.useEffect(() => {
    fetch('/api/me')
      .then((r) => r.json())
      .then((d) => setSignedIn(!!d?.authenticated))
      .catch(() => setSignedIn(false));
  }, []);

  // A 401 means "not signed in and no key": open the sign-in modal and queue
  // the interrupted action to re-run once the session cookie exists.
  const requestSignIn = React.useCallback((retry: (() => void) | null) => {
    retryRef.current = retry;
    setShowSignIn(true);
  }, []);

  const onAuthed = React.useCallback(() => {
    setSignedIn(true);
    setError(null);
    setGalleryRefresh((x) => x + 1);
    const retry = retryRef.current;
    retryRef.current = null;
    retry?.();
  }, []);

  // Persist successful generations to the user's media library so the
  // playground doubles as a searchable gallery of everything they've made.
  const saveGenerations = React.useCallback(
    (items: Array<{ kind: 'image' | 'video' | 'audio'; url: string; prompt?: string; title?: string }>) => {
      const savable = items.filter((i) => /^https?:/i.test(i.url));
      if (!savable.length) return;
      void Promise.all(
        savable.map((i) =>
          fetch('/api/media/items', {
            method: 'POST',
            headers: authHeaders(),
            credentials: 'include',
            body: JSON.stringify({ ...i, source: 'playground' }),
          }).catch(() => null),
        ),
      ).then(() => setGalleryRefresh((x) => x + 1));
    },
    [apiKey],
  );

  React.useEffect(() => {
    try {
      localStorage.setItem('appnz_gateway_sound', soundEnabled ? '1' : '0');
    } catch {
      /* storage blocked */
    }
  }, [soundEnabled]);

  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streaming]);

  // Group the catalogue by modality once (deduping repeated ids).
  const grouped = React.useMemo(() => {
    const g: Record<Modality, CatalogModel[]> = {
      chat: [], image: [], embeddings: [], video: [], music: [], speech: [], transcription: [], '3d': [],
    };
    const seen = new Set<string>();
    for (const m of models) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      const k = ((m.modality as Modality) && g[m.modality as Modality] ? m.modality : inferModality(m.id)) as Modality;
      g[k].push(m);
    }
    return g;
  }, [models]);

  const supportsAuto = tab === 'chat' || tab === 'image' || tab === 'video';
  const tabModels = grouped[tab];
  // Demo example for the selected surface+model — a real pre-generated output
  // (webp/opus/webm on appstatic) plus the exact inputs that produced it, so
  // every model opens showing what it does instead of an empty panel.
  const demoKey = `${tab}|${model}`;
  const demo: GatewayDemo | undefined = GATEWAY_DEMOS[demoKey];
  const videoSpec = React.useMemo(() => getVideoParamSpec(model), [model]);

  // Prefill prompt + reference image + video args from the demo when a model is
  // selected, so the surface opens ready-to-run with its hosted example.
  const lastDemoKey = React.useRef('');
  React.useEffect(() => {
    if (!demo || lastDemoKey.current === demoKey) return;
    lastDemoKey.current = demoKey;
    if (demo.prompt) {
      if (tab === 'speech') setTtsInput(demo.prompt);
      else if (tab === 'embeddings') setEmbedInput(demo.prompt);
      else setPrompt(demo.prompt);
    }
    if (tab === 'speech' && demo.voice) setVoice(demo.voice);
    if (demo.inputUrl) setAssetUrl(demo.inputUrl);
    if (tab === 'image' && demo.size) setSize(demo.size);
    if (tab === 'video') {
      setVideoResolution(demo.resolution || '720p');
      setVideoDuration(demo.duration || '4');
      setVideoAspect(demo.aspectRatio || '16:9');
      setVideoAudio(demo.generateAudio ?? false);
    }
  }, [demoKey, demo]);

  const videoBody = React.useCallback(
    () =>
      buildVideoPayload(
        model,
        {
          prompt,
          resolution: videoResolution,
          duration: videoDuration,
          aspectRatio: videoAspect,
          generateAudio: videoAudio,
          imageUrl: assetUrl,
        },
        videoAdvanced,
        videoSpec,
      ),
    [model, prompt, videoResolution, videoDuration, videoAspect, videoAudio, assetUrl, videoAdvanced, videoSpec],
  );

  const pickDefaultModel = React.useCallback(
    (t: Modality): string => {
      const list = grouped[t];
      if (t === 'chat') return 'auto';
      if (t === 'image') return 'auto';
      if (t === 'video') return list.find((m) => m.id.includes('auto'))?.id || 'auto';
      if (t === 'transcription') return ''; // server picks the STT provider chain
      return list[0]?.id || '';
    },
    [grouped],
  );

  // Reset model + result when switching surfaces.
  const switchTab = (t: Modality) => {
    if (t === tab) return;
    setTab(t);
    setModel(pickDefaultModel(t));
    setResult(null);
    setError(null);
    setLatencyMs(null);
    setResultView('preview');
    if (t === 'image') {
      const first = grouped.image.find((m) => m.supported_sizes?.length);
      setSize(first?.supported_sizes?.[0] || '1024x1024');
    }
  };

  const selectedModel = React.useMemo(() => tabModels.find((m) => m.id === model), [tabModels, model]);
  const sizeOptions = selectedModel?.supported_sizes?.length
    ? selectedModel.supported_sizes
    : ['1024x1024', '1024x768', '768x1024', '512x512'];
  const projectedChatTokens = React.useMemo(() => {
    const pending = chatInput.trim() || chatAttachments.length
      ? [makeChatMessage('user', chatInput.trim() || 'Please analyze the attached file.', { attachments: chatAttachments })]
      : [];
    return estimateChatTokens([...messages, ...pending], system);
  }, [messages, chatInput, chatAttachments, system]);
  const projectedContextPct = selectedModel?.context_window
    ? Math.min(100, Math.round((projectedChatTokens / selectedModel.context_window) * 100))
    : null;
  const projectedChatCost = React.useMemo(() => {
    if (tab !== 'chat' || !selectedModel) return null;
    const input = ((selectedModel.input_price_per_1m || 0) * projectedChatTokens) / 1_000_000;
    const output = ((selectedModel.output_price_per_1m || 0) * maxTokens) / 1_000_000;
    const total = input + output;
    return total > 0 ? total : null;
  }, [tab, selectedModel, projectedChatTokens, maxTokens]);

  const persistKey = (k: string) => {
    setApiKey(k);
    try {
      if (k) localStorage.setItem('appnz_gateway_key', k);
      else localStorage.removeItem('appnz_gateway_key');
    } catch {
      /* storage blocked */
    }
  };

  const authHeaders = (): Record<string, string> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (apiKey.trim()) h.Authorization = `Bearer ${apiKey.trim()}`;
    return h;
  };

  const setBlob = (url: string | null) => {
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    blobRef.current = url;
  };
  React.useEffect(() => () => setBlob(null), []);

  const onUpload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const url = await uploadFile(file);
      setAssetUrl(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };

  const uploadChatFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    setChatUploading(true);
    setChatUploadError(null);
    try {
      const next: ChatAttachment[] = [];
      for (const file of list) {
        const kind = inferAttachmentKind(file);
        let extractedText = '';
        let extractedPreview = '';
        if (kind === 'document') {
          try {
            const extracted = await extractDocument(file);
            extractedText = extracted.text;
            extractedPreview = extracted.preview;
          } catch {
            if (file.type.startsWith('text/') || /\.(txt|md|csv|json|jsonl|html|css|js|ts|tsx|py|go|rs|sql|log)$/i.test(file.name)) {
              extractedText = await file.slice(0, 120_000).text();
              extractedPreview = extractedText.slice(0, 700);
            }
          }
        }
        let url = '';
        try {
          url = await uploadFile(file);
        } catch (uploadErr) {
          if (!extractedText) throw uploadErr;
        }
        next.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name: file.name || 'upload',
          type: file.type || 'application/octet-stream',
          size: file.size,
          url: url || undefined,
          kind,
          extractedText,
          extractedPreview,
        });
      }
      setChatAttachments((prev) => [...prev, ...next]);
    } catch (e) {
      setChatUploadError(e instanceof Error ? e.message : String(e));
    } finally {
      setChatUploading(false);
      if (chatFileInputRef.current) chatFileInputRef.current.value = '';
    }
  };

  const removeChatAttachment = (id: string) => {
    setChatAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  const stop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
  };

  // ---- Chat (streaming) --------------------------------------------------
  const streamChatHistory = async (history: ChatMessage[]) => {
    if (!history.length || streaming || chatUploading) return;
    setError(null);
    setChatUploadError(null);
    setUsage(null);
    setLatencyMs(null);
    setMessages([...history, makeChatMessage('assistant', '')]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    const start = performance.now();
    const apiMessages = [
      ...(system.trim() ? [{ role: 'system', content: system.trim() }] : []),
      ...history.map(messageForAPI),
    ];

    try {
      // Relative URL on purpose: the playground must hit THIS origin so the
      // session cookie (host-only __Host-appnz_sso_session) is sent. Using the
      // `origin` prop here would post cross-origin on beta/www/localhost (where
      // it briefly holds the hardcoded prod origin) and drop the cookie → 401.
      const resp = await fetch('/v1/chat/completions', {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({ model, messages: apiMessages, stream: true, max_tokens: maxTokens }),
      });
      if (!resp.ok || !resp.body) throw new Error(await errorDetail(resp));

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let acc = '';
      let promptTok = 0;
      let completionTok = 0;
      let cachedTok = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const payload = trimmed.slice(5).trim();
          if (!payload || payload === '[DONE]') continue;
          let chunk: any;
          try {
            chunk = JSON.parse(payload);
          } catch {
            continue;
          }
          if (chunk.appnz) continue;
          const delta = chunk?.choices?.[0]?.delta?.content;
          if (delta) {
            acc += delta;
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { ...(next[next.length - 1] || makeChatMessage('assistant', '')), role: 'assistant', content: acc };
              return next;
            });
          }
          if (chunk?.usage) {
            promptTok = chunk.usage.prompt_tokens || promptTok;
            completionTok = chunk.usage.completion_tokens || completionTok;
            cachedTok = chunk.usage.prompt_cache_hit_tokens || chunk.usage.prompt_tokens_details?.cached_tokens || cachedTok;
          }
        }
      }
      setLatencyMs(Math.round(performance.now() - start));
      if (promptTok || completionTok) setUsage({ prompt: promptTok, completion: completionTok, cached: cachedTok || undefined });
      if (soundEnabled) playCompletionSound();
      if (!acc) {
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = { ...(next[next.length - 1] || makeChatMessage('assistant', '')), role: 'assistant', content: '(no content returned)' };
          return next;
        });
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        setError(String(e?.message || e));
        setMessages((prev) => prev.slice(0, -1));
        if (String(e?.message || '').startsWith('401')) requestSignIn(() => void streamChatHistory(history));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if ((!text && !chatAttachments.length) || streaming || chatUploading) return;

    const userMessage = makeChatMessage('user', text || 'Please analyze the attached file.', {
      attachments: chatAttachments,
    });
    const history = [...messages, userMessage];
    setChatInput('');
    setChatAttachments([]);
    await streamChatHistory(history);
  };

  const regenerateMessage = (index: number) => {
    if (streaming || chatUploading) return;
    const target = messages[index];
    if (!target) return;
    const cut = target.role === 'user' ? index + 1 : index;
    const history = messages.slice(0, cut);
    if (!history.some((m) => m.role === 'user')) return;
    void streamChatHistory(history);
  };

  const editMessage = (index: number, content: string) => {
    const nextContent = content.trim();
    if (!nextContent || streaming || chatUploading) return;
    const target = messages[index];
    if (!target) return;
    const edited = messages.map((m, i) => (i === index ? { ...m, content: nextContent, createdAt: m.createdAt || Date.now() } : m));

    if (target.role === 'user') {
      const history = edited.slice(0, index + 1);
      void streamChatHistory(history);
      return;
    }

    setMessages(edited);
  };

  const forkMessage = (index: number) => {
    if (streaming || chatUploading) return;
    const target = messages[index];
    if (!target) return;
    const branch = messages.slice(0, index + 1).map((m, i) => ({
      ...m,
      createdAt: m.createdAt || Date.now(),
      branchOf: i === index ? index : m.branchOf,
    }));
    setMessages(branch);
  };

  const deleteMessage = (index: number) => {
    if (streaming || chatUploading) return;
    setMessages((prev) => prev.filter((_, i) => i !== index));
  };

  const clearChat = () => {
    stop();
    setMessages([]);
    setUsage(null);
    setLatencyMs(null);
    setError(null);
  };

  // ---- Non-chat surfaces -------------------------------------------------
  const run = async () => {
    if (busy) return;
    setError(null);
    setResult(null);
    setLatencyMs(null);
    setResultView('preview');
    setBusy(true);
    const start = performance.now();
    try {
      if (tab === 'transcription') await runTranscription();
      else if (tab === 'speech') await runSpeech();
      else if (tab === 'image') await runImage();
      else if (tab === 'embeddings') await runEmbeddings();
      else await runMedia(tab); // video | music | 3d
      setLatencyMs(Math.round(performance.now() - start));
      if (soundEnabled) playCompletionSound();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      if (msg.startsWith('401')) requestSignIn(() => void run());
    } finally {
      setBusy(false);
    }
  };

  const runImage = async () => {
    const resp = await fetch('/v1/images/generations', {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify({ model, prompt, size, n: 1 }),
    });
    if (!resp.ok) throw new Error(await errorDetail(resp));
    const json = await resp.json();
    const images: string[] = (json?.data || [])
      .map((d: any) => d?.url || (d?.b64_json ? `data:image/png;base64,${d.b64_json}` : null))
      .filter(Boolean);
    setResult({ kind: 'image', json, images, raw: pretty(json) });
    saveGenerations(images.map((url) => ({ kind: 'image' as const, url, prompt })));
  };

  const runEmbeddings = async () => {
    const resp = await fetch('/v1/embeddings', {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify({ model, input: embedInput }),
    });
    if (!resp.ok) throw new Error(await errorDetail(resp));
    const json = await resp.json();
    const embedding: number[] = json?.data?.[0]?.embedding || [];
    setResult({ kind: 'embeddings', json, embedding, raw: pretty(json) });
  };

  const runMedia = async (kind: Modality) => {
    if (kind === '3d' && !assetUrl) throw new Error('Upload an image first — image→3D needs a source image.');
    let body: Record<string, unknown>;
    if (kind === '3d') {
      body = { model, image_url: assetUrl };
    } else if (kind === 'video') {
      body = videoBody();
    } else {
      body = { model, prompt };
      if (assetUrl) body.image_url = assetUrl;
    }
    const resp = await fetch(ENDPOINT[kind], {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw new Error(await errorDetail(resp));
    const json = await resp.json();
    const media = findMedia(json);
    setResult({
      kind,
      json,
      raw: pretty(json),
      mediaUrl: media?.url,
      mediaKind: media?.kind ?? '',
    });
    if (media?.url && (media.kind === 'image' || media.kind === 'video' || media.kind === 'audio')) {
      saveGenerations([{ kind: media.kind, url: media.url, prompt }]);
    }
  };

  const runSpeech = async () => {
    const resp = await fetch('/v1/audio/speech', {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify({ model, voice, input: ttsInput }),
    });
    if (!resp.ok) throw new Error(await errorDetail(resp));
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    setBlob(url);
    setResult({
      kind: 'speech',
      blobUrl: url,
      mediaKind: 'audio',
      blobNote: `${blob.type || 'audio'} · ${(blob.size / 1024).toFixed(1)} KB`,
      raw: `# binary audio response\n${blob.type || 'audio/mpeg'}\n${blob.size} bytes`,
    });
  };

  const runTranscription = async () => {
    if (!audioFile) throw new Error('Choose an audio file to transcribe.');
    const headers: Record<string, string> = { 'Content-Type': audioFile.type || 'audio/webm' };
    if (apiKey.trim()) headers.Authorization = `Bearer ${apiKey.trim()}`;
    const qs = language.trim() ? `?language=${encodeURIComponent(language.trim())}` : '';
    const resp = await fetch(`/v1/audio/transcriptions${qs}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: audioFile,
    });
    if (!resp.ok) throw new Error(await errorDetail(resp));
    const json = await resp.json();
    setResult({ kind: 'transcription', json, text: extractTranscript(json), raw: pretty(json) });
  };

  // ---- View-code (multi-language) ----------------------------------------
  const codeSamples = React.useMemo(() => {
    const common = { origin, path: ENDPOINT[tab], apiKey };
    switch (tab) {
      case 'chat':
        return buildGatewaySamples({ ...common, sdk: 'chat', body: { model, messages: [messageForAPI({ role: 'user', content: chatInput || (chatAttachments.length ? 'Please analyze the attached file.' : '...'), attachments: chatAttachments })], stream: true, max_tokens: maxTokens } });
      case 'image':
        return buildGatewaySamples({ ...common, sdk: 'image', body: { model, prompt, size, n: 1 } });
      case 'embeddings':
        return buildGatewaySamples({ ...common, sdk: 'embeddings', body: { model, input: embedInput } });
      case 'speech':
        return buildGatewaySamples({ ...common, sdk: 'speech', body: { model, voice, input: ttsInput } });
      case 'transcription':
        return buildGatewaySamples({ ...common, sdk: 'transcription', langQuery: language.trim() || undefined });
      case 'video':
        return buildGatewaySamples({ ...common, sdk: 'post', body: videoBody() });
      case 'music':
        return buildGatewaySamples({ ...common, sdk: 'post', body: assetUrl ? { model, prompt, image_url: assetUrl } : { model, prompt } });
      case '3d':
        return buildGatewaySamples({ ...common, sdk: 'post', body: { model, image_url: assetUrl || 'https://appstatic.app.nz/app/static/uploads/…' } });
      default:
        return buildGatewaySamples({ ...common, sdk: 'post', body: { model } });
    }
  }, [tab, origin, apiKey, model, chatInput, chatAttachments, maxTokens, prompt, size, embedInput, voice, ttsInput, language, assetUrl, videoBody]);

  const codeMarkdown = React.useMemo(
    () => samplesToMarkdown(codeSamples, `${tab} — app.nz gateway`),
    [codeSamples, tab],
  );

  const imageCost =
    tab === 'image' && selectedModel?.price_per_image ? selectedModel.price_per_image : null;
  const chatCost = React.useMemo(() => {
    if (tab !== 'chat' || !usage || !selectedModel) return null;
    const cached = Math.max(0, Math.min(usage.prompt, usage.cached || 0));
    const uncached = Math.max(0, usage.prompt - cached);
    const inputRate = selectedModel.input_price_per_1m || 0;
    const cachedRate = selectedModel.input_cache_hit_per_1m ?? inputRate;
    const inC = ((inputRate * uncached) + (cachedRate * cached)) / 1_000_000;
    const outC = ((selectedModel.output_price_per_1m || 0) * usage.completion) / 1_000_000;
    const total = inC + outC;
    return total > 0 ? total : null;
  }, [tab, usage, selectedModel]);

  // ---- Render ------------------------------------------------------------
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="gateway-playground">
      {/* Modality tabs */}
      <div className="mb-4 flex flex-wrap gap-1.5" role="tablist">
        {TABS.map(({ key, label, icon: Icon }) => {
          const count = grouped[key].length;
          const activeTab = key === tab;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={activeTab}
              onClick={() => switchTab(key)}
              data-testid={`gateway-playground-tab-${key}`}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
                activeTab
                  ? 'border-white/20 bg-sky-400/15 text-sky-200'
                  : 'border-white/10 bg-white/[0.03] text-white/65 hover:text-white'
              }`}
            >
              <Icon size={14} /> {label}
              {count > 0 && (
                <span className={activeTab ? 'text-black/45' : 'text-white/30'}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Model + code toggle + settings */}
      <div className="mb-3 grid gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <label className="block">
          <span className="mb-1 block text-xs font-mono text-white/40">Model</span>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            data-testid="gateway-playground-model"
            disabled={tab === 'transcription'}
            className={`${inputCx} disabled:opacity-50`}
          >
            {tab === 'transcription' && <option value="">auto (server picks STT provider)</option>}
            {supportsAuto && <option value="auto">auto (smart routing)</option>}
            {tabModels.map((m) => (
              <option key={m.id} value={m.id}>
                {m.id}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={() => setShowCode((v) => !v)}
          className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-white/10 px-3 text-xs font-mono text-white/60 hover:text-white"
        >
          <Code2 size={14} /> {showCode ? 'Hide code' : 'View code'}
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowSettings((v) => !v)}
            data-testid="gateway-playground-settings"
            aria-expanded={showSettings}
            className={`inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border px-3 text-xs font-mono transition-colors ${
              showSettings ? 'border-white/25 bg-white/[0.06] text-white' : 'border-white/10 text-white/60 hover:text-white'
            }`}
          >
            <Settings2 size={14} /> Settings
            <span className={signedIn ? 'text-emerald-300/70' : apiKey ? 'text-sky-300/70' : 'text-white/30'}>
              {signedIn ? '· session' : apiKey ? '· key' : ''}
            </span>
          </button>
          {showSettings && (
            <div className="absolute right-0 top-11 z-30 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-white/15 bg-[#0b0b0c] p-3 shadow-[0_18px_60px_rgba(0,0,0,0.6)]">
              <label className="block">
                <span className="mb-1 block text-xs font-mono text-white/40">API key (optional when signed in)</span>
                <input
                  value={apiKey}
                  onChange={(e) => persistKey(e.target.value)}
                  type="password"
                  placeholder="pk_live_…"
                  data-testid="gateway-playground-key"
                  className={inputCx}
                />
              </label>
              <p className="mt-1.5 text-[11px] leading-4 text-white/40">
                {signedIn
                  ? 'Signed in — requests use your session automatically. A key overrides it.'
                  : 'Stored locally in this browser. Or sign in and skip keys entirely.'}
              </p>
              {signedIn === false && (
                <button
                  type="button"
                  onClick={() => {
                    setShowSettings(false);
                    requestSignIn(null);
                  }}
                  className="mt-2 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-sky-400 text-xs font-bold text-black transition-colors hover:bg-sky-300"
                >
                  <LogIn size={13} /> Sign in
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setSoundEnabled((v) => !v);
                  if (!soundEnabled) playCompletionSound();
                }}
                className={`mt-2 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border text-xs font-mono transition-colors ${
                  soundEnabled
                    ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
                    : 'border-white/10 text-white/60 hover:text-white'
                }`}
                title="Play a sound when a gateway response finishes"
              >
                <Volume2 size={14} /> Sound on completion {soundEnabled ? 'on' : 'off'}
              </button>
            </div>
          )}
        </div>
      </div>

      {showCode && (
        <div className="mb-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {CODE_LANGS.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => setCodeLang(l.id)}
                className={`rounded px-2.5 py-1 text-xs font-mono transition-colors ${codeLang === l.id ? 'bg-white text-black' : 'border border-white/10 text-white/50 hover:text-white'}`}
                data-testid={`gateway-code-lang-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(codeMarkdown);
                  setCopiedMd(true);
                  window.setTimeout(() => setCopiedMd(false), 1600);
                } catch {
                  /* clipboard blocked */
                }
              }}
              className="ml-auto inline-flex items-center gap-1.5 rounded border border-white/10 px-2.5 py-1 text-xs font-mono text-white/55 hover:text-white"
              data-testid="gateway-code-copy-md"
            >
              {copiedMd ? <Check size={13} className="text-emerald-300" /> : <Copy size={13} />}
              {copiedMd ? 'Copied' : 'Copy as Markdown'}
            </button>
          </div>
          <CodeBlock code={codeSamples[codeLang] || ''} language={codeLangHl[codeLang]} testId="gateway-playground-code" />
        </div>
      )}

      {/* Per-surface controls */}
      {tab === 'chat' && (
        <div className="mb-3 grid gap-3 lg:grid-cols-[1fr_18rem]">
          <label className="block">
            <span className="mb-1 flex items-center justify-between text-xs font-mono text-white/40">
              <span>System prompt</span>
              <button
                type="button"
                onClick={() => setSystemExpanded((v) => !v)}
                data-testid="gateway-playground-system-expand"
                className="inline-flex items-center gap-1 text-white/40 transition-colors hover:text-white"
                aria-label={systemExpanded ? 'Collapse system prompt' : 'Expand system prompt'}
              >
                {systemExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                {systemExpanded ? 'collapse' : 'expand'}
              </button>
            </span>
            <textarea
              value={system}
              onChange={(e) => setSystem(e.target.value)}
              rows={systemExpanded ? 8 : 1}
              className="w-full resize-none rounded-lg border border-white/10 bg-[#050505] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-white/25 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 flex justify-between text-xs font-mono text-white/40">
              <span>Max tokens</span>
              <span className="text-white/60">{maxTokens.toLocaleString()}</span>
            </span>
            <input
              type="range"
              min="256"
              max={Math.min(32768, Math.max(4096, selectedModel?.max_output_tokens || 4096))}
              step="256"
              value={maxTokens}
              onChange={(e) => setMaxTokens(parseInt(e.target.value))}
              className="h-1 w-full accent-white"
              data-testid="gateway-playground-max-tokens"
            />
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono text-white/35">
              <span>
                ctx {fmtTokens(projectedChatTokens)}
                {selectedModel?.context_window ? `/${fmtTokens(selectedModel.context_window)}` : ''}
                {projectedContextPct !== null ? ` (${projectedContextPct}%)` : ''}
              </span>
              {projectedChatCost !== null && <span className="text-amber-300/70">est {fmtCost(projectedChatCost)}</span>}
            </div>
          </label>
        </div>
      )}

      {(tab === 'image' || tab === 'video' || tab === 'music') && (
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-mono text-white/40">Prompt</span>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={2}
            className="w-full resize-none rounded-lg border border-white/10 bg-[#050505] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-white/25 focus:outline-none"
          />
        </label>
      )}

      {tab === 'video' && (
        <div className="mb-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <label className="block">
              <span className="mb-1 block text-xs font-mono text-white/40">Resolution</span>
              <select value={videoResolution} onChange={(e) => setVideoResolution(e.target.value)} className={inputCx} data-testid="video-resolution">
                {videoSpec.resolutions.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-mono text-white/40">Duration</span>
              <select value={videoDuration} onChange={(e) => setVideoDuration(e.target.value)} className={inputCx} data-testid="video-duration">
                {videoSpec.durations.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-mono text-white/40">Aspect</span>
              <select value={videoAspect} onChange={(e) => setVideoAspect(e.target.value)} className={inputCx} data-testid="video-aspect-ratio">
                {videoSpec.aspectRatios.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-mono text-white/40">{videoSpec.enableSafetyChecker ? 'Safety' : 'Audio'}</span>
              <button
                type="button"
                onClick={() => videoSpec.generateAudio && setVideoAudio((v) => !v)}
                className={`h-10 w-full rounded-lg border px-3 text-xs font-mono transition-colors ${(videoSpec.enableSafetyChecker || videoAudio) ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200' : 'border-white/10 bg-[#050505] text-white/50'}`}
                data-testid="video-generate-audio"
              >
                {videoSpec.enableSafetyChecker || videoAudio ? 'on' : 'off'}
              </button>
            </label>
          </div>
          <button type="button" onClick={() => setShowVideoAdvanced((v) => !v)} className="mt-2 text-[11px] font-mono uppercase tracking-wider text-white/40 hover:text-white" data-testid="video-advanced-toggle">
            {showVideoAdvanced ? '− Advanced args' : '+ Advanced args'}
          </button>
          {showVideoAdvanced && (
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="video-advanced">
              {videoSpec.negativePrompt && (
                <label className="block col-span-2 sm:col-span-1">
                  <span className="mb-1 block text-xs font-mono text-white/40">Negative prompt</span>
                  <input value={videoAdvanced.negativePrompt} onChange={(e) => setVideoAdvanced((p) => ({ ...p, negativePrompt: e.target.value }))} placeholder="blurry, distorted" className={inputCx} data-testid="video-negative-prompt" />
                </label>
              )}
              {videoSpec.seed && (
                <label className="block">
                  <span className="mb-1 block text-xs font-mono text-white/40">Seed</span>
                  <input value={videoAdvanced.seed} onChange={(e) => setVideoAdvanced((p) => ({ ...p, seed: e.target.value }))} inputMode="numeric" placeholder="random" className={inputCx} data-testid="video-seed" />
                </label>
              )}
              {videoSpec.numFrames && (
                <label className="block">
                  <span className="mb-1 block text-xs font-mono text-white/40">Num frames</span>
                  <input value={videoAdvanced.numFrames} onChange={(e) => setVideoAdvanced((p) => ({ ...p, numFrames: e.target.value }))} inputMode="numeric" placeholder={videoSpec.numFrames.placeholder} className={inputCx} data-testid="video-num-frames" />
                </label>
              )}
              {videoSpec.framesPerSecond && (
                <label className="block">
                  <span className="mb-1 block text-xs font-mono text-white/40">FPS</span>
                  <select value={videoAdvanced.framesPerSecond} onChange={(e) => setVideoAdvanced((p) => ({ ...p, framesPerSecond: e.target.value }))} className={inputCx} data-testid="video-fps">
                    <option value="">default</option>
                    {videoSpec.framesPerSecond.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              )}
              {videoSpec.guidanceScale && (
                <label className="block">
                  <span className="mb-1 block text-xs font-mono text-white/40">Guidance</span>
                  <input value={videoAdvanced.guidanceScale} onChange={(e) => setVideoAdvanced((p) => ({ ...p, guidanceScale: e.target.value }))} inputMode="decimal" placeholder={videoSpec.guidanceScale.placeholder} className={inputCx} data-testid="video-guidance-scale" />
                </label>
              )}
              {videoSpec.numInferenceSteps && (
                <label className="block">
                  <span className="mb-1 block text-xs font-mono text-white/40">Steps</span>
                  <input value={videoAdvanced.numInferenceSteps} onChange={(e) => setVideoAdvanced((p) => ({ ...p, numInferenceSteps: e.target.value }))} inputMode="numeric" placeholder={videoSpec.numInferenceSteps.placeholder} className={inputCx} data-testid="video-num-inference-steps" />
                </label>
              )}
              {videoSpec.outputFormats && (
                <label className="block">
                  <span className="mb-1 block text-xs font-mono text-white/40">Format</span>
                  <select value={videoAdvanced.outputFormat} onChange={(e) => setVideoAdvanced((p) => ({ ...p, outputFormat: e.target.value }))} className={inputCx} data-testid="video-output-format">
                    <option value="">default</option>
                    {videoSpec.outputFormats.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'image' && (
        <label className="mb-3 block max-w-[200px]">
          <span className="mb-1 block text-xs font-mono text-white/40">Size</span>
          <select value={size} onChange={(e) => setSize(e.target.value)} className={inputCx}>
            {sizeOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      )}

      {tab === 'embeddings' && (
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-mono text-white/40">Input text</span>
          <textarea
            value={embedInput}
            onChange={(e) => setEmbedInput(e.target.value)}
            rows={2}
            className="w-full resize-none rounded-lg border border-white/10 bg-[#050505] px-3 py-2 text-sm text-white focus:border-white/25 focus:outline-none"
          />
        </label>
      )}

      {tab === 'speech' && (
        <div className="mb-3 grid gap-2 sm:grid-cols-[160px_1fr]">
          <label className="block">
            <span className="mb-1 block text-xs font-mono text-white/40">Voice</span>
            <input value={voice} onChange={(e) => setVoice(e.target.value)} className={inputCx} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-mono text-white/40">Text to speak</span>
            <input value={ttsInput} onChange={(e) => setTtsInput(e.target.value)} className={inputCx} />
          </label>
        </div>
      )}

      {/* Reference/source image upload for image (optional), video (optional), 3d (required) */}
      {(tab === 'image' || tab === 'video' || tab === '3d') && (
        <div className="mb-3">
          <span className="mb-1 block text-xs font-mono text-white/40">
            {tab === '3d' ? 'Source image (required)' : tab === 'image' ? 'Reference image (optional — used by image-to-image models)' : 'Source image (optional — enables image-to-video)'}
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-white/10 bg-[#050505] px-3 text-sm text-white/70 hover:text-white">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              {uploading ? 'Uploading…' : 'Upload image'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onUpload(e.target.files?.[0])}
              />
            </label>
            {SAMPLE_I2V_IMAGES.map((img) => (
              <button
                key={img.src}
                type="button"
                title={img.label}
                onClick={() => setAssetUrl(img.src)}
                className={`relative h-10 w-10 overflow-hidden rounded-md border transition-colors ${assetUrl === img.src ? 'border-white/60' : 'border-white/10 hover:border-white/30'}`}
              >
                <img src={img.src} alt={img.label} className="h-full w-full object-cover" />
              </button>
            ))}
            {assetUrl && !SAMPLE_I2V_IMAGES.some(i => i.src === assetUrl) && (
              <div className="flex items-center gap-2">
                <img src={assetUrl} alt="source" className="h-10 w-10 rounded-md border border-white/10 object-cover" />
                <button onClick={() => setAssetUrl('')} className="text-white/40 hover:text-white" aria-label="Remove image">
                  <X size={15} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'transcription' && (
        <div className="mb-3 grid gap-2 sm:grid-cols-[1fr_160px]">
          <label className="block">
            <span className="mb-1 block text-xs font-mono text-white/40">Audio file</span>
            <label className="flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-white/10 bg-[#050505] px-3 text-sm text-white/70 hover:text-white">
              <Upload size={15} /> {audioFile ? audioFile.name : 'Choose audio…'}
              <input
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
              />
            </label>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-mono text-white/40">Language (optional)</span>
            <input value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="en" className={inputCx} />
          </label>
        </div>
      )}

      {/* Result panel */}
      <ResultPanel
        tab={tab}
        result={result}
        busy={busy}
        streaming={streaming}
        messages={messages}
        onRegenerate={regenerateMessage}
        onEdit={editMessage}
        onFork={forkMessage}
        onDelete={deleteMessage}
        scrollRef={scrollRef}
        view={resultView}
        setView={setResultView}
        demo={demo}
      />

      {error && (
        <div
          className="mt-3 rounded-lg border border-red-500/20 bg-red-500/5 p-3 text-sm text-red-300"
          data-testid="gateway-playground-error"
        >
          {error}
        </div>
      )}

      {/* Action row */}
      <div className="mt-3 flex flex-wrap items-end gap-2">
        {tab === 'chat' ? (
          <>
            <div className="flex min-w-[min(100%,260px)] flex-1 flex-col gap-2">
              {(chatAttachments.length > 0 || chatUploadError) && (
                <div className="flex flex-wrap gap-2">
                  {chatAttachments.map((att) => (
                    <div
                      key={att.id}
                      className="inline-flex max-w-full items-center gap-2 rounded-lg border border-white/10 bg-[#050505] px-2.5 py-1.5 text-xs font-mono text-white/60"
                      title={attachmentSummary(att)}
                    >
                      {att.kind === 'video' ? (
                        <Film size={14} className="shrink-0" />
                      ) : att.kind === 'image' ? (
                        <ImageIcon size={14} className="shrink-0" />
                      ) : att.kind === 'document' ? (
                        <FileText size={14} className="shrink-0" />
                      ) : (
                        <FileIcon size={14} className="shrink-0" />
                      )}
                      {att.url ? (
                        <a href={att.url} target="_blank" rel="noreferrer" className="truncate hover:text-white">
                          {att.name}
                        </a>
                      ) : (
                        <span className="truncate">{att.name}</span>
                      )}
                      <span className="shrink-0 text-white/30">{formatBytes(att.size)}</span>
                      {att.extractedPreview && <span className="hidden text-emerald-300/60 sm:inline">text ready</span>}
                      <button
                        type="button"
                        onClick={() => removeChatAttachment(att.id)}
                        className="rounded p-0.5 text-white/30 hover:bg-white/10 hover:text-white"
                        aria-label={`Remove ${att.name}`}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                  {chatUploadError && (
                    <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-1.5 text-xs text-red-300">
                      {chatUploadError}
                    </div>
                  )}
                </div>
              )}
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChat();
                  }
                }}
                rows={2}
                placeholder="Message or attach a file… (Enter to send, Shift+Enter for newline)"
                data-testid="gateway-playground-input"
                className="resize-none rounded-lg border border-white/10 bg-[#050505] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-white/25 focus:outline-none"
              />
            </div>
            <button
              type="button"
              onClick={() => chatFileInputRef.current?.click()}
              disabled={chatUploading || streaming}
              className="inline-flex h-11 items-center gap-1.5 rounded-lg border border-white/15 px-3 text-sm font-semibold text-white/65 hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              title="Attach PDF, document, image, video, audio, or file"
              data-testid="gateway-playground-attach"
            >
              {chatUploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
            </button>
            <input
              ref={chatFileInputRef}
              type="file"
              multiple
              className="hidden"
              accept={`${DOCUMENT_ACCEPT},image/*,video/*,audio/*,.js,.jsx,.ts,.tsx,.py,.go,.rs,.sql,.log`}
              onChange={(e) => e.target.files && uploadChatFiles(e.target.files)}
            />
            {streaming ? (
              <button
                onClick={stop}
                className="inline-flex h-11 items-center gap-1.5 rounded-lg border border-white/15 px-4 text-sm font-semibold text-white hover:bg-white/5"
              >
                <Square size={15} /> Stop
              </button>
            ) : (
              <>
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={clearChat}
                  className="inline-flex h-11 items-center gap-1.5 rounded-lg border border-white/15 px-3 text-sm font-semibold text-white/60 hover:bg-white/5 hover:text-white"
                  title="Clear chat"
                  data-testid="gateway-playground-clear"
                >
                  <Trash2 size={15} />
                </button>
              )}
              <button
                onClick={sendChat}
                data-testid="gateway-playground-send"
                disabled={(!chatInput.trim() && !chatAttachments.length) || chatUploading}
                className="inline-flex h-11 items-center gap-1.5 rounded-lg bg-white px-4 text-sm font-bold text-black transition-colors hover:bg-white/86 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Send size={15} /> Send
              </button>
              </>
            )}
          </>
        ) : (
          <button
            onClick={run}
            disabled={busy || uploading}
            data-testid="gateway-playground-run"
            className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-lg bg-sky-400 px-4 text-sm font-bold text-black transition-colors hover:bg-sky-300 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
            {busy ? 'Running…' : `Run ${TABS.find((t) => t.key === tab)?.label}`}
          </button>
        )}
      </div>

      {(latencyMs != null || usage || imageCost) && (
        <div className="mt-2 flex flex-wrap gap-3 text-xs font-mono text-white/40" data-testid="gateway-playground-stats">
          {latencyMs != null && <span>{latencyMs} ms</span>}
          {usage && <span>{usage.prompt} in / {usage.completion} out tokens</span>}
          {usage?.cached ? <span className="text-cyan-300/70">cache {fmtTokens(usage.cached)}</span> : null}
          {chatCost != null && <span>~{fmtCost(chatCost)}</span>}
          {imageCost != null && <span>~{fmtCost(imageCost)} / image</span>}
        </div>
      )}

      {tab !== 'chat' && tab !== 'embeddings' && tab !== 'transcription' && (
        <GenerationsPanel
          signedIn={signedIn}
          refreshKey={galleryRefresh}
          onRequireSignIn={() => requestSignIn(null)}
          onUsePrompt={(p) => setPrompt(p)}
        />
      )}

      {showSignIn && (
        <React.Suspense fallback={null}>
          <SignInModal
            onClose={() => setShowSignIn(false)}
            onAuthed={onAuthed}
            message="Sign in to run this request with your account — no API key needed. Your generations are saved to your library."
          />
        </React.Suspense>
      )}
    </div>
  );
};

// GenerationsPanel: the signed-in user's saved generations (media library),
// searchable by prompt/title, shown under the media surfaces so the playground
// doubles as a gallery of everything they've made.
const GenerationsPanel: React.FC<{
  signedIn: boolean | null;
  refreshKey: number;
  onRequireSignIn: () => void;
  onUsePrompt: (prompt: string) => void;
}> = ({ signedIn, refreshKey, onRequireSignIn, onUsePrompt }) => {
  type LibItem = {
    id: string;
    kind: string;
    url: string;
    thumbUrl?: string;
    posterUrl?: string;
    prompt?: string;
    title?: string;
    createdAt?: string;
  };
  const [items, setItems] = React.useState<LibItem[] | null>(null);
  const [query, setQuery] = React.useState('');
  const [kind, setKind] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    setLoading(true);
    const t = window.setTimeout(() => {
      const params = new URLSearchParams({ limit: '60' });
      if (query.trim()) params.set('q', query.trim());
      if (kind) params.set('kind', kind);
      fetch(`/api/media/library?${params}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((d) => {
          if (!cancelled) setItems(Array.isArray(d?.items) ? d.items : []);
        })
        .catch(() => {
          if (!cancelled) setItems([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [signedIn, refreshKey, query, kind]);

  const remove = async (id: string) => {
    setItems((prev) => (prev || []).filter((i) => i.id !== id));
    await fetch(`/api/media/items?id=${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' }).catch(
      () => null,
    );
  };

  return (
    <div className="mt-4 border-t border-white/10 pt-3" data-testid="gateway-playground-generations">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-xs font-mono uppercase tracking-[0.14em] text-white/40">Your generations</span>
        {signedIn ? (
          <>
            <div className="relative ml-auto w-full min-w-0 sm:w-60">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search prompts…"
                data-testid="gateway-generations-search"
                className="h-8 w-full rounded-lg border border-white/10 bg-[#050505] pl-8 pr-2.5 text-xs text-white placeholder:text-white/30 focus:border-white/25 focus:outline-none"
              />
            </div>
            <div className="flex gap-1">
              {[
                ['', 'All'],
                ['image', 'Image'],
                ['video', 'Video'],
                ['audio', 'Audio'],
              ].map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`rounded px-2 py-1 text-[11px] font-mono transition-colors ${
                    kind === k ? 'bg-sky-400/15 text-sky-200' : 'text-white/40 hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <a href="/art" className="text-[11px] font-mono text-white/35 hover:text-white">
              Full library →
            </a>
          </>
        ) : (
          <button
            type="button"
            onClick={onRequireSignIn}
            data-testid="gateway-generations-signin"
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-white/12 px-2.5 py-1.5 text-xs font-semibold text-white/70 transition-colors hover:border-white/30 hover:text-white"
          >
            <LogIn size={13} /> Sign in to see your generations
          </button>
        )}
      </div>

      {signedIn && (
        <>
          {loading && !items && <div className="py-4 text-center text-xs text-white/35">Loading…</div>}
          {items && items.length === 0 && (
            <div className="py-4 text-center text-xs text-white/35">
              {query ? `Nothing matches “${query}”.` : 'Run a generation and it lands here automatically.'}
            </div>
          )}
          {items && items.length > 0 && (
            <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-5 lg:grid-cols-8">
              {items.map((it) => (
                <div key={it.id} className="group relative overflow-hidden rounded-lg border border-white/10 bg-[#050505]">
                  <a href={it.url} target="_blank" rel="noreferrer" title={it.prompt || it.title || it.kind}>
                    {it.kind === 'video' ? (
                      <video
                        src={it.url}
                        poster={it.posterUrl || it.thumbUrl || undefined}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        className="aspect-square w-full object-cover"
                        onMouseEnter={(e) => void (e.currentTarget as HTMLVideoElement).play().catch(() => null)}
                        onMouseLeave={(e) => (e.currentTarget as HTMLVideoElement).pause()}
                      />
                    ) : it.kind === 'audio' ? (
                      <div className="flex aspect-square w-full flex-col items-center justify-center gap-1 p-2 text-white/45">
                        <Music size={18} />
                        <span className="line-clamp-2 text-center text-[10px] leading-3">{it.title || it.prompt || 'audio'}</span>
                      </div>
                    ) : (
                      <img
                        src={it.thumbUrl || it.url}
                        alt={it.prompt || it.title || 'generation'}
                        loading="lazy"
                        className="aspect-square w-full object-cover"
                      />
                    )}
                  </a>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                    <div className="line-clamp-2 text-[10px] leading-3 text-white/80">{it.prompt || it.title || ''}</div>
                  </div>
                  <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    {it.prompt && (
                      <button
                        type="button"
                        onClick={() => onUsePrompt(it.prompt || '')}
                        title="Use this prompt"
                        className="rounded bg-black/70 p-1 text-white/70 hover:text-white"
                      >
                        <RotateCcw size={12} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void remove(it.id)}
                      title="Remove from library"
                      className="rounded bg-black/70 p-1 text-white/70 hover:text-red-300"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ResultPanel renders the live preview (per modality) and a raw-JSON toggle.
const ResultPanel: React.FC<{
  tab: Modality;
  result: RunResult | null;
  busy: boolean;
  streaming: boolean;
  messages: ChatMessage[];
  onRegenerate: (index: number) => void;
  onEdit: (index: number, content: string) => void;
  onFork: (index: number) => void;
  onDelete: (index: number) => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  view: 'preview' | 'json';
  setView: (v: 'preview' | 'json') => void;
  demo?: GatewayDemo;
}> = ({ tab, result, busy, streaming, messages, onRegenerate, onEdit, onFork, onDelete, scrollRef, view, setView, demo }) => {
  if (tab === 'chat') {
    return (
      <div
        ref={scrollRef}
        data-testid="gateway-playground-transcript"
        className="max-h-[420px] min-h-[180px] overflow-y-auto rounded-xl border border-white/10 bg-[#050505] p-3"
      >
        {messages.length === 0 && !demo?.reply ? (
          <div className="py-10 text-center text-sm text-white/35">Send a message to try the gateway.</div>
        ) : messages.length === 0 && demo?.reply ? (
          <div className="space-y-3 py-2" data-testid="gateway-chat-demo">
            {demo.prompt && (
              <div className="text-right">
                <div className="ml-auto inline-block max-w-[92%] whitespace-pre-wrap rounded-lg bg-white px-3 py-2 text-left text-sm text-black shadow-sm">
                  {demo.prompt}
                </div>
              </div>
            )}
            <div className="text-left">
              <div className="inline-block max-w-[92%] whitespace-pre-wrap rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/85 shadow-sm">
                {demo.reply}
              </div>
            </div>
            <div className="pt-1 text-center text-xs text-white/35">Example reply — send a message to get your own.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((m, i) => (
              <ChatBubble
                key={`${m.role}-${i}-${m.createdAt || 0}`}
                message={m}
                index={i}
                streaming={streaming}
                isLast={i === messages.length - 1}
                onRegenerate={onRegenerate}
                onEdit={onEdit}
                onFork={onFork}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  const caption = 'Example output — run to generate your own.';
  return (
    <div className="rounded-xl border border-white/10 bg-[#050505]" data-testid="gateway-playground-result">
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
        <span className="text-xs font-mono text-white/40">Result</span>
        {(result || demo?.mediaUrl) && (
          <div className="flex gap-1">
            {(['preview', 'json'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`rounded px-2 py-1 text-xs font-mono transition-colors ${
                  view === v ? 'bg-sky-400/15 text-sky-200' : 'text-white/40 hover:text-white'
                }`}
              >
                {v === 'preview' ? 'Preview' : 'JSON'}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="min-h-[140px] p-3">
        {busy ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-white/40">
            <Loader2 size={16} className="animate-spin" /> Running…
          </div>
        ) : result ? (
          view === 'json' && result.raw ? (
            <CodeBlock code={result.raw} language="json" testId="gateway-playground-json" />
          ) : (
            <ResultPreview result={result} />
          )
        ) : demo ? (
          <DemoPreview demo={demo} caption={caption} />
        ) : (
          <div className="py-10 text-center text-sm text-white/35">Run a request to see the result here.</div>
        )}
      </div>
    </div>
  );
};

// DemoPreview renders the hosted example for the selected model before any run.
const DemoPreview: React.FC<{ demo: GatewayDemo; caption: string }> = ({ demo, caption }) => {
  if (demo.mediaUrl && demo.mediaKind === 'video') {
    const dur = demo.duration ? `${demo.duration}s ` : '';
    const res = demo.resolution ? `${demo.resolution} · ` : '';
    return (
      <figure className="space-y-2" data-testid="gateway-demo">
        <video controls loop muted playsInline poster={demo.posterUrl} src={demo.mediaUrl} className="w-full rounded-lg border border-white/10" />
        <figcaption className="text-xs text-white/35">Example {res}{dur}output — run to generate your own.</figcaption>
      </figure>
    );
  }
  if (demo.mediaUrl && demo.mediaKind === 'image') {
    return (
      <figure className="space-y-2" data-testid="gateway-demo">
        <img src={demo.mediaUrl} alt={demo.prompt || 'Example output'} className="mx-auto max-h-[420px] w-auto max-w-full rounded-lg border border-white/10" />
        <figcaption className="text-center text-xs text-white/35">{caption}</figcaption>
      </figure>
    );
  }
  if (demo.mediaUrl && demo.mediaKind === 'audio') {
    return (
      <figure className="space-y-3 py-4" data-testid="gateway-demo">
        {demo.transcript !== undefined && <div className="mb-1 text-xs font-mono text-white/35">sample input</div>}
        <audio controls src={demo.mediaUrl} className="w-full" />
        <figcaption className="text-xs text-white/35">{demo.transcript ?? caption}</figcaption>
      </figure>
    );
  }
  if (demo.mediaUrl && demo.mediaKind === 'model') {
    return (
      <figure className="space-y-2 py-6" data-testid="gateway-demo">
        <div className="flex items-center justify-center gap-2 py-4 text-white/45"><Box size={20} /></div>
        <figcaption className="text-center text-xs text-white/35">
          Example GLB{demo.note ? ` · ${demo.note}` : ''} —{' '}
          <a href={demo.mediaUrl} target="_blank" rel="noreferrer" className="underline hover:text-white">open asset</a>{' '}
          · run to generate your own.
        </figcaption>
      </figure>
    );
  }
  if (demo.embedDims) {
    return (
      <div className="space-y-2 py-6 text-sm" data-testid="gateway-demo">
        <div className="font-mono text-xs text-white/40">{demo.embedDims} dims · first 8:</div>
        <div className="font-mono text-xs leading-relaxed text-white/70">
          [{(demo.embedPreview || []).map((n) => n.toFixed(4)).join(', ')}, …]
        </div>
        <div className="text-xs text-white/35">{caption}</div>
      </div>
    );
  }
  return <div className="py-10 text-center text-sm text-white/35">Run a request to see the result here.</div>;
};

const ChatBubble: React.FC<{
  message: ChatMessage;
  index: number;
  streaming: boolean;
  isLast: boolean;
  onRegenerate: (index: number) => void;
  onEdit: (index: number, content: string) => void;
  onFork: (index: number) => void;
  onDelete: (index: number) => void;
}> = ({ message, index, streaming, isLast, onRegenerate, onEdit, onFork, onDelete }) => {
  const [copied, setCopied] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(message.content);
  const isUser = message.role === 'user';
  const isStreamingThis = streaming && isLast && !isUser;

  React.useEffect(() => {
    if (!editing) setDraft(message.content);
  }, [editing, message.content]);

  const copy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  const save = () => {
    const next = draft.trim();
    if (next && next !== message.content) onEdit(index, next);
    setEditing(false);
  };

  return (
    <div className={isUser ? 'text-right' : 'text-left'}>
      <div className={`group inline-flex max-w-[92%] flex-col gap-1.5 ${isUser ? 'items-end' : 'items-start'}`}>
        <div className="flex items-center gap-2 text-[10px] font-mono text-white/25">
          {message.branchOf != null && <span className="rounded border border-white/10 px-1.5 py-0.5">forked</span>}
          {messageTime(message.createdAt)}
        </div>
        {editing ? (
          <div className="w-[min(620px,82vw)] rounded-lg border border-white/10 bg-black/80 p-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={Math.min(8, Math.max(3, draft.split('\n').length))}
              autoFocus
              className="w-full resize-y rounded-md border border-white/10 bg-[#050505] px-3 py-2 text-sm text-white outline-none focus:border-white/25"
              data-testid="gateway-playground-edit-input"
            />
            <div className="mt-2 flex justify-end gap-2">
              <button
                onClick={() => {
                  setDraft(message.content);
                  setEditing(false);
                }}
                className="rounded border border-white/10 px-2 py-1 text-xs font-mono text-white/45 hover:text-white"
              >
                Cancel
              </button>
              <button onClick={save} disabled={!draft.trim()} className="rounded bg-white px-2 py-1 text-xs font-mono font-bold text-black disabled:opacity-40">
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <div
              className={`whitespace-pre-wrap rounded-lg px-3 py-2 text-sm shadow-sm ${
                isUser ? 'bg-white text-black' : 'border border-white/10 bg-white/[0.04] text-white/85'
              }`}
            >
              {message.content || (isStreamingThis ? <Loader2 size={14} className="animate-spin" /> : '')}
            </div>
            {!isStreamingThis && (
              <div className={`flex items-center gap-0.5 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 ${isUser ? 'justify-end' : ''}`}>
                {message.content && (
                  <button onClick={copy} className="rounded-md p-1.5 text-white/30 hover:bg-white/10 hover:text-white" title="Copy message" data-testid="gateway-msg-copy">
                    {copied ? <Check size={14} className="text-emerald-300" /> : <Copy size={14} />}
                  </button>
                )}
                {message.content && (
                  <button onClick={() => setEditing(true)} disabled={streaming} className="rounded-md p-1.5 text-white/30 hover:bg-white/10 hover:text-white disabled:opacity-40" title="Edit message" data-testid="gateway-msg-edit">
                    <Pencil size={14} />
                  </button>
                )}
                <button onClick={() => onRegenerate(index)} disabled={streaming} className="rounded-md p-1.5 text-white/30 hover:bg-white/10 hover:text-white disabled:opacity-40" title={isUser ? 'Resend from here' : 'Regenerate response'} data-testid="gateway-msg-regenerate">
                  <RotateCcw size={14} />
                </button>
                <button onClick={() => onFork(index)} disabled={streaming} className="rounded-md p-1.5 text-white/30 hover:bg-white/10 hover:text-white disabled:opacity-40" title="Fork from here" data-testid="gateway-msg-fork">
                  <GitFork size={14} />
                </button>
                <button onClick={() => onDelete(index)} disabled={streaming} className="rounded-md p-1.5 text-white/30 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-40" title="Delete message" data-testid="gateway-msg-delete">
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </>
        )}
        {message.attachments && message.attachments.length > 0 && (
          <div className="flex flex-col gap-1">
            {message.attachments.map((att) =>
              att.url ? (
                <a
                  key={att.id}
                  href={att.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex max-w-full items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] font-mono text-white/55 hover:border-white/25 hover:text-white"
                  title={attachmentSummary(att)}
                >
                  {att.kind === 'video' ? <Film size={13} /> : att.kind === 'image' ? <ImageIcon size={13} /> : att.kind === 'document' ? <FileText size={13} /> : <FileIcon size={13} />}
                  <span className="truncate">{att.name}</span>
                  <span className="shrink-0 text-white/25">{formatBytes(att.size)}</span>
                </a>
              ) : (
                <div
                  key={att.id}
                  className="inline-flex max-w-full items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] font-mono text-white/55"
                  title={attachmentSummary(att)}
                >
                  {att.kind === 'video' ? <Film size={13} /> : att.kind === 'image' ? <ImageIcon size={13} /> : att.kind === 'document' ? <FileText size={13} /> : <FileIcon size={13} />}
                  <span className="truncate">{att.name}</span>
                  <span className="shrink-0 text-white/25">{formatBytes(att.size)}</span>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ResultPreview: React.FC<{ result: RunResult }> = ({ result }) => {
  const r = result;
  if (r.kind === 'image') {
    if (!r.images?.length) return <Empty>No image data returned.</Empty>;
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {r.images.map((src, i) => (
          <img key={i} src={src} alt={`result ${i + 1}`} className="w-full rounded-lg border border-white/10" />
        ))}
      </div>
    );
  }

  if (r.kind === 'embeddings') {
    const emb = r.embedding || [];
    if (!emb.length) return <Empty>No embedding returned.</Empty>;
    const max = Math.max(...emb.slice(0, 64).map((v) => Math.abs(v)), 1e-6);
    return (
      <div>
        <div className="mb-2 text-sm text-white/70">
          <span className="font-mono text-white">{emb.length}</span> dimensions
        </div>
        <div className="flex h-16 items-center gap-px">
          {emb.slice(0, 96).map((v, i) => (
            <div
              key={i}
              title={String(v)}
              className={v >= 0 ? 'flex-1 bg-emerald-400/70' : 'flex-1 bg-rose-400/70'}
              style={{ height: `${Math.max(4, (Math.abs(v) / max) * 100)}%` }}
            />
          ))}
        </div>
        <div className="mt-2 font-mono text-xs text-white/40">
          [{emb.slice(0, 6).map((v) => v.toFixed(4)).join(', ')}, …]
        </div>
      </div>
    );
  }

  if (r.kind === 'transcription') {
    if (!r.text) return <Empty>No transcript returned.</Empty>;
    return <p className="whitespace-pre-wrap text-sm leading-7 text-white/85">{r.text}</p>;
  }

  if (r.kind === 'speech') {
    return (
      <div>
        {r.blobUrl ? <audio controls src={r.blobUrl} className="w-full" /> : <Empty>No audio returned.</Empty>}
        {r.blobNote && <div className="mt-2 font-mono text-xs text-white/40">{r.blobNote}</div>}
      </div>
    );
  }

  // video | music | 3d — rendered from a discovered asset URL.
  if (r.mediaUrl) {
    const k = r.mediaKind;
    if (k === 'video') return <video controls src={r.mediaUrl} className="w-full rounded-lg border border-white/10" />;
    if (k === 'audio') return <audio controls src={r.mediaUrl} className="w-full" />;
    if (k === 'image')
      return <img src={r.mediaUrl} alt="result" className="w-full rounded-lg border border-white/10" />;
    return (
      <a
        href={r.mediaUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.04] px-4 py-2 text-sm font-semibold text-white hover:bg-white hover:text-black"
      >
        <Box size={16} /> Open {r.kind === '3d' ? '3D asset' : 'result'}
      </a>
    );
  }

  return <Empty>Job submitted — no renderable asset URL in the response yet. See the JSON tab.</Empty>;
};

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="py-8 text-center text-sm text-white/40">{children}</div>
);

function pretty(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

async function errorDetail(resp: Response): Promise<string> {
  const text = await resp.text().catch(() => '');
  let detail = text.slice(0, 300);
  try {
    const j = JSON.parse(text);
    detail = j?.error?.message || j?.error?.detail || j?.error || j?.detail || detail;
  } catch {
    /* not json */
  }
  return `${resp.status}: ${detail || 'request failed'}`;
}
